import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  IconButton,
  Box,
  Chip,
  Avatar,
  Dialog,
  DialogContent,
  DialogTitle,
  DialogActions,
  Button,
} from '@mui/material';
import {
  ContentCopy,
  Image as ImageIcon,
  Message as MessageIcon,
  Delete,
  ZoomIn,
  Download,
  Send,
  InsertDriveFile,
} from '@mui/icons-material';
import { Message as MessageType } from '../services/api';
import { getFullUrl } from '../config/api.config';
import TextWithLinks from './TextWithLinks';
import { trackButtonClick, trackMessageAction } from '../utils/analytics';

interface MessageCardProps {
  message: MessageType;
  onCopy?: () => void;
  onDelete?: () => void;
  onForward?: () => void;
  showDeleteButton?: boolean;
  showForwardButton?: boolean;
}

const MessageCard: React.FC<MessageCardProps> = ({ message, onCopy, onDelete, onForward, showDeleteButton = false, showForwardButton = false }) => {
  const [imageDialogOpen, setImageDialogOpen] = React.useState(false);
  const [imageSize, setImageSize] = React.useState<number | null>(null);

  // Get files array - handle both old format (imageUrl) and new format (files array)
  const getFiles = () => {
    if (message.type === 'files' && message.files && message.files.length > 0) {
      return message.files;
    }
    // Backward compatibility: convert single imageUrl to files array
    if (message.type === 'image' && message.imageUrl) {
      return [{
        url: message.imageUrl,
        type: 'image/jpeg',
        extension: 'jpg',
        size: 0,
        name: 'image.jpg'
      }];
    }
    return [];
  };

  const isImageFile = (file: any) => file.type?.startsWith('image/');

  // Calculate image size for image messages
  React.useEffect(() => {
    const files = getFiles();
    if (files.length === 1 && isImageFile(files[0])) {
      const file = files[0];
      const calculateSize = async () => {
        try {
          if (file.size > 0) {
            // Use size from file info if available
            setImageSize(file.size);
          } else {
            // Fallback: calculate from URL
            const imageUrl = file.url;
            if (!imageUrl) return;
            
            if (imageUrl.startsWith('data:')) {
              // For base64 images, calculate size from base64 string
              const base64Data = imageUrl.split(',')[1];
              const sizeInBytes = Math.ceil(base64Data.length * 0.75); // Base64 to bytes conversion
              setImageSize(sizeInBytes);
            } else {
              // For server URLs, fetch and get blob size
              const response = await fetch(getFullUrl(imageUrl));
              const blob = await response.blob();
              setImageSize(blob.size);
            }
          }
        } catch (error) {
          console.error('Failed to calculate image size:', error);
        }
      };
      calculateSize();
    }
  }, [message.files, message.imageUrl, message.type]);

  // Format bytes to MB
  const formatImageSize = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  // Function to download file(s)
  const handleDownloadImage = async () => {
    const files = getFiles();
    if (files.length === 0) return;
    
    trackButtonClick('Download File', 'Message Card');
    trackMessageAction('download', message.type);
    
    try {
      // Download all files
      for (const file of files) {
        try {
          let blob: Blob;
          
          // Handle base64 data URLs (from local storage)
          if (file.url.startsWith('data:')) {
            const response = await fetch(file.url);
            blob = await response.blob();
          } else {
            // Handle server URLs
            const fullUrl = getFullUrl(file.url);
            const response = await fetch(fullUrl);
            blob = await response.blob();
          }
          
          // Create download link
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          
          // Use file name or generate one
          const filename = file.name || `file-${Date.now()}.${file.extension || 'jpg'}`;
          link.download = filename;
          
          // Trigger download
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          // Clean up
          window.URL.revokeObjectURL(url);
          
          // Add small delay between downloads
          if (files.length > 1) {
            await new Promise(resolve => setTimeout(resolve, 100));
          }
        } catch (error) {
          console.error(`Failed to download file ${file.name}:`, error);
        }
      }
    } catch (error) {
      console.error('Download failed:', error);
    }
  };
  
  const formatTimestamp = (timestamp: number) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
    return date.toLocaleDateString();
  };

  const getMessageIcon = () => {
    switch (message.type) {
      case 'image':
        return <ImageIcon />;
      case 'files':
        return <InsertDriveFile />;
      case 'text':
      default:
        return <MessageIcon />;
    }
  };

  const files = getFiles();

  return (
    <>
      <Card sx={{ mb: 0.5, backgroundColor: 'rgba(255, 255, 255, 0.12)', backdropFilter: 'blur(10px)' }}>
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
            <Avatar sx={{ bgcolor: '#2196F3', width: 32, height: 32 }}>
              {getMessageIcon()}
            </Avatar>
            
            <Box sx={{ flexGrow: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                <Typography variant="subtitle2" fontWeight="medium" sx={{ color: '#FFFFFF' }}>
                  {message.fromDeviceName || 'Unknown Device'}
                </Typography>
                <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                  {formatTimestamp(message.timestamp)}
                </Typography>
                {message.isRead && (
                  <Chip label="Read" size="small" color="success" variant="outlined" />
                )}
              </Box>
              
              {message.type === 'text' && message.text && (
                <Box sx={{ mb: 0.5 }}>
                  <TextWithLinks text={message.text} variant="body2" />
                </Box>
              )}
              
              {/* Files display - supports both single image (backward compatibility) and multiple files */}
              {(message.type === 'files' || message.type === 'image') && files.length > 0 && (
                <Box sx={{ mb: 0.5 }}>
                  {files.length === 1 && isImageFile(files[0]) ? (
                    // Single image display (backward compatibility)
                    <Box 
                      sx={{ 
                        position: 'relative',
                        cursor: 'pointer',
                        borderRadius: 1,
                        overflow: 'hidden',
                        maxWidth: 300,
                        '&:hover': {
                          opacity: 0.8,
                          transform: 'scale(1.02)',
                          transition: 'all 0.2s ease-in-out'
                        }
                      }}
                      onClick={() => {
                        trackButtonClick('View Image', 'Message Card');
                        setImageDialogOpen(true);
                      }}
                    >
                      <img
                        src={getFullUrl(files[0].url)}
                        alt="Received image"
                        style={{
                          width: '100%',
                          height: 'auto',
                          maxHeight: 200,
                          objectFit: 'cover',
                          display: 'block'
                        }}
                        onLoad={() => {
                          console.log('Image loaded successfully:', files[0].url?.substring(0, 50) + '...');
                        }}
                        onError={(e) => {
                          console.error('Image failed to load:', files[0].url);
                          const target = e.target as HTMLImageElement;
                          target.style.display = 'none';
                          target.parentElement!.innerHTML = `
                            <div style="padding: 8px; text-align: center; color: rgba(255, 255, 255, 0.8);">
                              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.8)' }}>
                                Failed to load image
                              </Typography>
                              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)', display: 'block', marginTop: '4px', wordBreak: 'break-all' }}>
                                URL: ${getFullUrl(files[0].url)}
                              </Typography>
                            </div>
                          `;
                        }}
                      />
                      <Box
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          bgcolor: 'rgba(0,0,0,0.5)',
                          borderRadius: '50%',
                          p: 0.5,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center'
                        }}
                      >
                        <ZoomIn sx={{ color: 'white', fontSize: 16 }} />
                      </Box>
                    </Box>
                  ) : (
                    // Multiple files display
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                      {files.map((file, index) => (
                        <Box
                          key={index}
                          sx={{
                            border: '1px solid rgba(255, 255, 255, 0.2)',
                            borderRadius: 1,
                            p: 1,
                            minWidth: 120,
                            maxWidth: 200,
                            backgroundColor: 'rgba(255, 255, 255, 0.05)',
                            cursor: isImageFile(file) ? 'pointer' : 'default',
                            '&:hover': isImageFile(file) ? {
                              opacity: 0.8,
                              transform: 'scale(1.02)',
                              transition: 'all 0.2s ease-in-out'
                            } : {}
                          }}
                          onClick={() => isImageFile(file) && setImageDialogOpen(true)}
                        >
                          {isImageFile(file) ? (
                            <img
                              src={getFullUrl(file.url)}
                              alt={file.name}
                              style={{
                                width: '100%',
                                height: 'auto',
                                maxHeight: 150,
                                objectFit: 'contain',
                                borderRadius: 4
                              }}
                            />
                          ) : (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <InsertDriveFile sx={{ fontSize: 32, color: 'rgba(255, 255, 255, 0.8)' }} />
                              <Box>
                                <Typography variant="caption" sx={{ display: 'block', wordBreak: 'break-word', color: '#FFFFFF' }}>
                                  {file.name}
                                </Typography>
                                {file.size > 0 && (
                                  <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                                    {formatImageSize(file.size)}
                                  </Typography>
                                )}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                  {/* Display total files count and size */}
                  {files.length > 1 && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'rgba(255, 255, 255, 0.65)' }}>
                      {files.length} file(s)
                      {(() => {
                        const totalSize = files.reduce((sum, file) => sum + (file.size || 0), 0);
                        return totalSize > 0 ? ` • ${formatImageSize(totalSize)}` : '';
                      })()}
                    </Typography>
                  )}
                  {/* Display image size for single image */}
                  {files.length === 1 && imageSize && (
                    <Typography variant="caption" sx={{ display: 'block', mt: 0.25, color: 'rgba(255, 255, 255, 0.65)' }}>
                      {formatImageSize(imageSize)}
                    </Typography>
                  )}
                </Box>
              )}
              
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              {message.isDelivered && (
                <Chip label="Delivered" size="small" color="info" variant="outlined" />
              )}
              {message.type === 'text' && onCopy && (
                <IconButton 
                  size="small" 
                  onClick={() => {
                    trackButtonClick('Copy Text', 'Message Card');
                    trackMessageAction('copy', 'text');
                    onCopy();
                  }} 
                  title="Copy to clipboard"
                >
                  <ContentCopy fontSize="small" />
                </IconButton>
              )}
              {(message.type === 'files' || message.type === 'image') && files.length > 0 && (
                <IconButton 
                  size="small" 
                  onClick={handleDownloadImage} 
                  title={files.length === 1 ? "Download file" : "Download files"}
                >
                  <Download fontSize="small" />
                </IconButton>
              )}
              {showForwardButton && onForward && (
                <IconButton 
                  size="small" 
                  onClick={() => {
                    trackButtonClick('Forward Message', 'Message Card');
                    trackMessageAction('forward', message.type);
                    onForward();
                  }} 
                  title="Forward to another device" 
                  color="primary"
                >
                  <Send fontSize="small" />
                </IconButton>
              )}
              {showDeleteButton && onDelete && (
                <IconButton 
                  size="small" 
                  onClick={() => {
                    trackButtonClick('Delete Message', 'Message Card');
                    trackMessageAction('delete', message.type);
                    onDelete();
                  }} 
                  title="Delete message"
                >
                  <Delete fontSize="small" />
                </IconButton>
              )}
            </Box>
            </Box>
          </Box>
        </CardContent>
      </Card>

      {/* Full-screen image dialog */}
      <Dialog
        open={imageDialogOpen}
        onClose={() => setImageDialogOpen(false)}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          {files.length === 1 ? 'Image' : 'Files'} from {message.fromDeviceName || 'Unknown Device'}
        </DialogTitle>
        <DialogContent>
          {files.length === 1 && isImageFile(files[0]) ? (
            <img
              src={getFullUrl(files[0].url)}
              alt="Full size image"
              style={{
                width: '100%',
                height: 'auto',
                maxHeight: '80vh',
                objectFit: 'contain'
              }}
              onLoad={() => {}}
              onError={(e) => {
                const target = e.target as HTMLImageElement;
                target.style.display = 'none';
                target.parentElement!.innerHTML = `
                  <div style="padding: 32px; text-align: center; color: rgba(255, 255, 255, 0.8);">
                    <Typography variant="body1" sx={{ color: 'rgba(255, 255, 255, 0.8)', marginBottom: '16px' }}>
                      Failed to load image
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)', display: 'block', wordBreak: 'break-all' }}>
                      URL: ${getFullUrl(files[0].url)}
                    </Typography>
                  </div>
                `;
              }}
            />
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
              {files.map((file, index) => (
                <Box key={index} sx={{ border: '1px solid rgba(255, 255, 255, 0.2)', borderRadius: 1, p: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
                  {isImageFile(file) ? (
                    <img
                      src={getFullUrl(file.url)}
                      alt={file.name}
                      style={{
                        width: '100%',
                        height: 'auto',
                        maxHeight: '60vh',
                        objectFit: 'contain'
                      }}
                    />
                  ) : (
                    <Box>
                      <Typography variant="body1" sx={{ color: '#FFFFFF' }}>{file.name}</Typography>
                      {file.size > 0 && (
                        <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.65)' }}>
                          {formatImageSize(file.size)}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={handleDownloadImage}
            startIcon={<Download />}
            variant="outlined"
          >
            Download Image
          </Button>
          <Button 
            onClick={() => setImageDialogOpen(false)}
            variant="contained"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default MessageCard;
